/**
 * @description: 用于埋点的指令
 * 曝光事件-默认只会上报一次-可以通过always修饰符来一直上报
 * v-track:eventName="eventData"
 * v-track:eventName.always="eventData"
 * 点击事件-默认可以多次上报-可以通过once修饰符来只上报一次
 * v-track:eventName.click="eventData"
 * v-track:eventName.click.once="eventData"
 */
class EventManager {
  private _map = new WeakMap();
  private _observer!: IntersectionObserver;

  public constructor() {
    this.clickListener = this.clickListener.bind(this);

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const list = this._map.get(el);
            if (!el || !list) {
              return;
            }

            for (let i = 0; i < list.length; i++) {
              const item = list[i];
              if (item.eventModifiers.click) {
                continue;
              }

              if (item.eventModifiers.always) {
                // 默认曝光事件只会上报一次，但是总有特殊场景需要一直上报，比如弹窗
              } else {
                this.remove(el, item);
              }

              const { eventName, eventData } = item;
              const data =
                typeof eventData === 'function' ? eventData() : eventData;
              window.hetaoTrack(eventName, data);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      },
    );
  }

  private clickListener(event: any) {
    const el = event.currentTarget;
    const list = this._map.get(el);
    if (!el || !list) {
      return;
    }

    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const { eventName, eventData, eventModifiers } = item;
      if (eventModifiers.click) {
        if (eventModifiers.once) {
          this.remove(el, item);
        }
        const data = typeof eventData === 'function' ? eventData() : eventData;
        window.hetaoTrack(eventName, data, event);
      }
    }
  }

  private observeClick(el: any) {
    el.addEventListener('click', this.clickListener, true);
  }

  private unobserveClick(el: any) {
    el.removeEventListener('click', this.clickListener, true);
  }

  public add(item: any) {
    const { el } = item;
    const list = this._map.get(el);

    if (list) {
      list.push(item);
    } else {
      this._map.set(el, [item]);
    }

    if (item.eventModifiers.click) {
      this.observeClick(el);
    } else {
      this._observer.observe(el);
    }
  }

  public remove(el: any, item?: any) {
    const list = this._map.get(el);
    if (list) {
      if (item) {
        this.removeItem(el, item);
        const newList = list.filter((i: any) => i !== item);
        if (newList.length) {
          this._map.set(el, newList);
        } else {
          this._map.delete(el);
        }
      } else {
        list.forEach((i: any) => this.removeItem(el, i));
        this._map.delete(el);
      }
    }
  }

  public removeItem(el: any, item: any) {
    if (item.eventModifiers.click) {
      this.unobserveClick(el);
    } else {
      this._observer.unobserve(el);
    }
  }
}

// 这是一个共享的全局单例对象
const eventManager = new EventManager();

export default {
  mounted(el: any, binding: any) {
    const eventName = binding.arg; // 必填
    const eventData = binding.value; // 可选
    const eventModifiers = binding.modifiers; // 可选，但是默认{}，所以不会为undefined

    eventManager.add({
      el,
      eventName,
      eventData,
      eventModifiers,
    });
  },

  unmounted(el: any) {
    eventManager.remove(el);
  },
};
